(() => {
  if (customElements.get('coffee-selector')) return;

  class CoffeeSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.detail = this.querySelector('[data-coffee-detail]');
      this.cards = [...this.querySelectorAll('[data-coffee-card]')];

      const script = this.querySelector('[data-coffee-products]');
      if (!script || !this.detail) return;

      try {
        this.data = JSON.parse(script.textContent);
      } catch (error) {
        console.error('Coffee Selector: JSON non valido.', error);
        return;
      }

      if (!Array.isArray(this.data.products) || !this.data.products.length) return;
      this.t = this.data.translations || {};

      this.cards.forEach((card) => {
        card.addEventListener('click', () => this.selectProduct(card.dataset.handle));
      });

      this.listToggle = this.querySelector('[data-coffee-list-toggle]');
      this.listToggle?.addEventListener('click', () => {
        const expanded = this.classList.toggle('coffee-selector--list-expanded');
        this.listToggle.setAttribute('aria-expanded', String(expanded));
        this.listToggle.textContent = expanded ? this.t.showLess : this.t.showAll;
      });

      const activeCard = this.cards.find((card) => card.classList.contains('is-active'));
      this.selectProduct(activeCard?.dataset.handle || this.data.products[0].handle);
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    }

    findFormatIndex() {
      return this.product.options.findIndex((option) => {
        const name = this.normalize(option.name);
        const values = option.values.map((value) => this.normalize(value));
        return name.includes('formato') ||
          name.includes('format') ||
          values.some((value) => value.includes('macinato') || value.includes('ground'));
      });
    }

    findGrindIndex() {
      return this.product.options.findIndex((option) => {
        const name = this.normalize(option.name);
        return name.includes('macinatura') ||
          name.includes('preparazione') ||
          name.includes('grind') ||
          name.includes('preparation');
      });
    }

    isGround(value) {
      const normalized = this.normalize(value);
      return normalized.includes('macinato') || normalized.includes('ground');
    }

    isBeans(value) {
      const normalized = this.normalize(value);
      return normalized.includes('grani') ||
        normalized.includes('chicchi') ||
        normalized.includes('beans') ||
        normalized.includes('whole bean');
    }

    is500g(value) {
      const normalized = this.normalize(value).replace(/\s+/g, '');
      return normalized.includes('500g') ||
        normalized.includes('0,5kg') ||
        normalized.includes('0.5kg');
    }

    selectProduct(handle) {
      this.product = this.data.products.find((item) => item.handle === handle) || this.data.products[0];
      this.formatIndex = this.findFormatIndex();
      this.grindIndex = this.findGrindIndex();
      this.selectedStandaloneGrind = 'Moka';

      const beans500Variant = this.product.variants.find((variant) => {
        return variant.available &&
          variant.options.some((value) => this.isBeans(value)) &&
          variant.options.some((value) => this.is500g(value));
      });

      const beansVariant = this.product.variants.find((variant) => {
        return variant.available && variant.options.some((value) => this.isBeans(value));
      });

      const weight500Variant = this.product.variants.find((variant) => {
        return variant.available && variant.options.some((value) => this.is500g(value));
      });

      const initialVariant = beans500Variant ||
        beansVariant ||
        weight500Variant ||
        this.product.variants.find((variant) => variant.available) ||
        this.product.variants[0];
      this.selected = [...(initialVariant?.options || [])];

      this.cards.forEach((card) => {
        const active = card.dataset.handle === this.product.handle;
        card.classList.toggle('is-active', active);
        card.setAttribute('aria-pressed', String(active));
      });

      this.render();
    }

    rating(label, rawValue) {
      const value = Math.max(0, Math.min(5, Number(rawValue) || 0));
      const row = document.createElement('div');
      row.className = 'coffee-profile-row';

      const name = document.createElement('span');
      name.className = 'coffee-profile-label';
      name.textContent = label;

      const dots = document.createElement('span');
      dots.className = 'coffee-profile-dots';
      dots.setAttribute('aria-label', `${value} ${this.t.outOfFive}`);

      for (let index = 1; index <= 5; index += 1) {
        const dot = document.createElement('span');
        dot.className = `coffee-profile-dot${index <= value ? ' is-active' : ''}`;
        dot.setAttribute('aria-hidden', 'true');
        dots.append(dot);
      }

      row.append(name, dots);
      return row;
    }

    render() {
      this.detail.setAttribute('aria-busy', 'true');
      this.detail.replaceChildren();

      const article = document.createElement('article');
      article.className = 'coffee-product';
      article.innerHTML = `
        <header class="coffee-product__heading" data-heading></header>
        <div class="coffee-product__summary" data-summary></div>
        <div class="coffee-product__profile" data-profile></div>
        <div class="coffee-product__configurator coffee-configurator" data-configurator></div>
        <div class="coffee-product__grind" data-grind hidden></div>
        <div class="coffee-product__purchase" data-purchase></div>
      `;

      if (this.product.featuredImage) {
        const image = document.createElement('img');
        image.className = 'coffee-product__image';
        image.src = this.product.featuredImage;
        image.alt = this.product.featuredImageAlt || this.product.title;
        image.width = 240;
        image.height = 240;
        image.loading = 'eager';
        article.querySelector('[data-summary]').append(image);
      }

      article.querySelector('[data-heading]').append(
        this.element('h2', 'coffee-title', this.product.title)
      );
      this.renderSummary(article.querySelector('[data-summary]'));
      this.renderProfile(article.querySelector('[data-profile]'));
      this.renderConfigurator(article.querySelector('[data-configurator]'));

      const grindContainer = article.querySelector('[data-grind]');
      const grindFieldset = article.querySelector('.coffee-option--grind');

      if (grindContainer && grindFieldset) {
        grindContainer.append(grindFieldset);
        grindContainer.hidden = false;
      }

      this.renderPurchase(article.querySelector('[data-purchase]'));

      this.detail.append(article);
      this.detail.setAttribute('aria-busy', 'false');

      this.addButton = article.querySelector('[data-add]');
      this.addButton.addEventListener('click', () => this.addToCart());
      this.quantityInput = article.querySelector('[data-quantity]');
      article.querySelector('[data-quantity-minus]').addEventListener('click', () => this.changeQuantity(-1));
      article.querySelector('[data-quantity-plus]').addEventListener('click', () => this.changeQuantity(1));
      this.quantityInput.addEventListener('change', () => this.changeQuantity(0));
      this.refreshVariant();
    }

    renderSummary(summary) {
      if (this.product.badge) summary.append(this.element('span', 'coffee-badge', this.product.badge));
      if (this.product.composition) summary.append(this.element('div', 'coffee-composition', this.product.composition));
      if (this.product.shortDescription) summary.append(this.element('p', 'coffee-description', this.product.shortDescription));
    }

    renderProfile(profile) {
      profile.append(this.element('h3', 'coffee-section-title', this.t.aromaticProfile));

      const rows = document.createElement('div');
      rows.className = 'coffee-profile';
      [
        [this.t.intensity, this.product.intensity],
        [this.t.body, this.product.body],
        [this.t.acidity, this.product.acidity],
        [this.t.sweetness, this.product.sweetness]
      ].forEach(([label, value]) => rows.append(this.rating(label, value)));
      profile.append(rows);

      if (this.product.aromas) {
        const aromas = document.createElement('div');
        aromas.className = 'coffee-aromas';
        aromas.append(
          this.element('h3', 'coffee-section-title', this.t.aromaticNotes),
          this.element('p', '', this.product.aromas)
        );
        profile.append(aromas);
      }

    }

    renderPurchase(purchase) {
      purchase.classList.add('coffee-purchase');
      purchase.innerHTML = `
        <p class="coffee-purchase__summary" data-selection-summary></p>
        <div class="coffee-purchase__actions">
          <div class="coffee-quantity">
            <label class="coffee-quantity__label" for="${this.id}-quantity">${this.t.quantity}</label>
            <div class="coffee-quantity__control">
              <button type="button" data-quantity-minus aria-label="${this.t.decreaseQuantity}">−</button>
              <input id="${this.id}-quantity" class="coffee-quantity__input" data-quantity type="number" min="1" max="99" value="1" inputmode="numeric">
              <button type="button" data-quantity-plus aria-label="${this.t.increaseQuantity}">+</button>
            </div>
          </div>
          <div class="coffee-price">
            <span class="coffee-price__current" data-price></span>
            <s class="coffee-price__compare" data-compare hidden></s>
          </div>
          <button type="button" class="button button--primary coffee-add" data-add></button>
        </div>
        <p class="coffee-message" data-message role="status"></p>
      `;
    }

    changeQuantity(delta) {
      const current = parseInt(this.quantityInput.value, 10) || 1;
      this.quantityInput.value = Math.max(1, Math.min(99, current + delta));
      this.refreshPurchase();
    }

    formatMoney(cents) {
      const locale = document.documentElement.lang || 'it-IT';
      const currency = this.data.currencyCode || 'EUR';

      try {
        if (currency === 'EUR') {
          const amount = new Intl.NumberFormat(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(cents / 100);
          return `€\u00a0${amount}`;
        }

        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
      } catch (error) {
        return this.variant?.price || '';
      }
    }

    refreshPurchase() {
      if (!this.quantityInput) return;

      const quantity = Math.max(1, Math.min(99, parseInt(this.quantityInput.value, 10) || 1));
      this.quantityInput.value = quantity;

      const total = this.variant?.priceAmount != null
        ? this.formatMoney(Number(this.variant.priceAmount) * quantity)
        : (this.variant?.price || '—');
      const summaryValues = this.selected.filter((value) => value && value !== 'Default Title');

      if (this.formatIndex >= 0 && this.isGround(this.selected[this.formatIndex]) && this.grindIndex < 0) {
        summaryValues.push(this.selectedStandaloneGrind);
      }

      this.querySelector('[data-selection-summary]').textContent = [this.product.title, ...summaryValues].join(' · ');
      this.querySelector('[data-price]').textContent = total;
      this.addButton.disabled = !this.variant?.available;
      this.addButton.textContent = this.variant?.available
        ? this.t.addToCart
        : this.t.unavailable;
    }

    renderConfigurator(config) {
      config.append(this.element('h3', 'coffee-section-title', this.t.preferenceTitle));
      this.optionInputs = [];

      const hasOnlyDefaultOption = this.product.options.length === 1 &&
        this.product.options[0].values.length === 1 &&
        this.product.options[0].values[0] === 'Default Title';

      if (!hasOnlyDefaultOption) {
        this.product.options.forEach((option, index) => config.append(this.renderOption(option, index)));
      }

      if (this.grindIndex < 0) {
        config.append(this.renderStandaloneGrind());
      }
    }

    renderStandaloneGrind() {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'coffee-option coffee-option--grind';
      fieldset.dataset.standaloneGrind = '';

      const legend = document.createElement('legend');
      legend.className = 'coffee-option__legend';
      legend.textContent = this.t.grindTitle;

      fieldset.append(
        legend,
        this.element('p', 'coffee-option__hint', this.t.grindHint)
      );

      const values = document.createElement('div');
      values.className = 'coffee-option__values';

      ['Moka', 'Espresso / Bar', this.t.turkishCoffee, this.t.filterCoffee].forEach((value, valueIndex) => {
        const id = `${this.id}-grind-${valueIndex}`;
        const input = document.createElement('input');
        const label = document.createElement('label');
        const image = document.createElement('img');

        input.type = 'radio';
        input.className = 'coffee-option__input';
        input.name = `${this.id}-standalone-grind`;
        input.id = id;
        input.value = value;
        input.checked = value === this.selectedStandaloneGrind;
        input.addEventListener('change', () => {
          this.selectedStandaloneGrind = value;
          this.refreshPurchase();
        });

        label.className = 'coffee-option__label';
        label.htmlFor = id;

        image.className = 'coffee-option__image';
        image.src = this.grindImage(value);
        image.alt = '';
        image.width = 110;
        image.height = 110;
        image.loading = 'lazy';

        label.append(
          image,
          this.element('span', 'coffee-option__text', value),
          this.element('span', 'coffee-option__check', '✓')
        );

        values.append(input, label);
      });

      fieldset.append(values);
      return fieldset;
    }

    grindImage(value) {
      const normalized = this.normalize(value);

      if (normalized.includes('moka')) {
        return this.dataset.grindMokaImage;
      }

      if (normalized.includes('espresso') || normalized.includes('bar')) {
        return this.dataset.grindEspressoImage;
      }

      if (normalized.includes('turc') || normalized.includes('turk')) {
        return this.dataset.grindTurcoImage;
      }

      if (normalized.includes('american') || normalized.includes('filtro') || normalized.includes('filter')) {
        return this.dataset.grindAmericanoImage;
      }

      return this.dataset.grindEspressoImage;
    }

    renderOption(option, optionIndex) {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'coffee-option';
      fieldset.dataset.optionIndex = optionIndex;

      const isGrind = optionIndex === this.grindIndex;
      if (isGrind) fieldset.classList.add('coffee-option--grind');
      const normalizedOptionName = this.normalize(option.name);
      if (normalizedOptionName.includes('peso') || normalizedOptionName.includes('weight')) {
        fieldset.classList.add('coffee-option--weight');
      }

      const legend = document.createElement('legend');
      legend.className = 'coffee-option__legend';
      legend.textContent = isGrind ? this.t.grindTitle : option.name;
      fieldset.append(legend);

      if (isGrind) {
        fieldset.append(this.element('p', 'coffee-option__hint', this.t.grindHint));
      }

      const values = document.createElement('div');
      values.className = 'coffee-option__values';

      option.values.forEach((value, valueIndex) => {
        const id = `${this.id}-option-${optionIndex}-${valueIndex}`;
        const input = document.createElement('input');
        input.type = 'radio';
        input.className = 'coffee-option__input';
        input.name = `${this.id}-option-${optionIndex}`;
        input.id = id;
        input.value = value;
        input.checked = this.selected[optionIndex] === value;
        input.dataset.optionIndex = optionIndex;
        input.addEventListener('change', () => this.selectOption(optionIndex, value));

        const label = document.createElement('label');
        label.className = 'coffee-option__label';
        label.htmlFor = id;

        if (isGrind) {
          const image = document.createElement('img');
          image.className = 'coffee-option__image';
          image.src = this.grindImage(value);
          image.alt = '';
          image.width = 110;
          image.height = 110;
          image.loading = 'lazy';

          label.append(
            image,
            this.element('span', 'coffee-option__text', value),
            this.element('span', 'coffee-option__check', '✓')
          );
        } else {
          label.textContent = value;
        }

        values.append(input, label);
        this.optionInputs.push(input);
      });

      fieldset.append(values);
      return fieldset;
    }

    selectOption(optionIndex, value) {
      const candidates = this.product.variants.filter((variant) => {
        return variant.available && variant.options[optionIndex] === value;
      });

      const bestVariant = candidates.sort((first, second) => {
        const score = (variant) => variant.options.reduce((total, optionValue, index) => {
          return total + (index !== optionIndex && optionValue === this.selected[index] ? 1 : 0);
        }, 0);
        return score(second) - score(first);
      })[0];

      if (bestVariant) this.selected = [...bestVariant.options];
      else this.selected[optionIndex] = value;

      this.syncOptionInputs();
      this.refreshVariant();
    }

    syncOptionInputs() {
      this.optionInputs.forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        input.checked = input.value === this.selected[index];
      });
    }

    updateConditionalOptions() {
      const grindFieldset = this.querySelector('.coffee-option--grind');
      if (!grindFieldset) return;

      const grindContainer = grindFieldset.closest('[data-grind]');

      const groundSelected = this.formatIndex < 0 || this.isGround(this.selected[this.formatIndex]);
      grindFieldset.hidden = !groundSelected;
      grindFieldset.setAttribute('aria-hidden', String(!groundSelected));

      if (grindContainer) {
        grindContainer.hidden = !groundSelected;
      }
    }

    refreshVariant() {
      this.variant = this.product.variants.find((variant) => {
        return variant.options.every((value, index) => value === this.selected[index]);
      });

      this.optionInputs.forEach((input) => {
        const index = Number(input.dataset.optionIndex);
        input.disabled = !this.product.variants.some((variant) => {
          return variant.available && variant.options[index] === input.value;
        });
      });

      this.syncOptionInputs();
      this.updateConditionalOptions();

      const compare = this.querySelector('[data-compare]');
      compare.hidden = !this.variant?.compareAtPrice;
      compare.textContent = this.variant?.compareAtPrice || '';

      this.refreshPurchase();
    }

    async addToCart() {
      if (!this.variant?.available || this.addButton.disabled) return;

      const quantityInput = this.querySelector('[data-quantity]');
      const quantity = Math.max(1, Math.min(99, parseInt(quantityInput.value, 10) || 1));
      quantityInput.value = quantity;

      const message = this.querySelector('[data-message]');
      const cart = document.querySelector('cart-notification');
      const body = {
        id: this.variant.id,
        quantity
      };

      const groundSelected =
        this.formatIndex >= 0 &&
        this.isGround(this.selected[this.formatIndex]);

      if (groundSelected && this.grindIndex < 0) {
        body.properties = {
          [this.t.grindProperty]: this.selectedStandaloneGrind
        };
      }

      if (cart?.getSectionsToRender) {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      this.addButton.disabled = true;
      this.addButton.textContent = this.t.adding;
      message.textContent = '';
      message.className = 'coffee-message';

      try {
        const response = await fetch(this.data.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.description || this.t.addError);

        if (cart?.renderContents && result.sections) {
          cart.renderContents(result);
        } else {
          window.location.assign(this.data.cartUrl);
          return;
        }

        message.textContent = this.t.added;
        message.classList.add('is-success');
      } catch (error) {
        message.textContent = error.message;
        message.classList.add('is-error');
      } finally {
        this.refreshPurchase();
      }
    }

    element(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      node.textContent = text ?? '';
      return node;
    }
  }

  customElements.define('coffee-selector', CoffeeSelector);
})();
